import { useCallback, useEffect, useState } from 'react';

import { istEinrichtungscode } from '../../domain/qrNutzlast';
import { useT } from '../../i18n';
import { canScan, scanCandidateCode } from '../../platform/scanner';
import { selectStation, useAppStore } from '../../store/useAppStore';
import { Icon } from '../../ui/Icon';
import styles from './Submit.module.css';

/** Sekunden, nach denen ohne Scan nachgefragt wird. */
const TIMEOUT_SECONDS = 120;

/**
 * Bühne für den Prüflingsausweis.
 *
 * Das eigentliche Scannen macht seit dem 2026-09-14 unsere **eigene** Vollbildbühne
 * (`QrScannerViewController.swift`, s. `platform/scanner.ts`). Diese Bühne hier bleibt
 * trotzdem: sie hält den Stationszusammenhang, das Zeitfenster und den Weg zurück, und sie ist
 * der Ort, an dem ein unbekannter Ausweis gemeldet wird. Auf dem Gerät startet der Scan beim Öffnen von selbst — einen zusätzlichen Tipp zu
 * verlangen wäre im Prüfungsbetrieb reine Verzögerung.
 */
export function ScannerScreen() {
  const t = useT();
  const station = useAppStore(selectStation);
  const closeSheet = useAppStore((s) => s.closeSheet);
  const openSheet = useAppStore((s) => s.openSheet);
  const goToNext = useAppStore((s) => s.goToNextCandidate);

  const selectByCode = useAppStore((s) => s.selectCandidateByCode);

  const [left, setLeft] = useState(TIMEOUT_SECONDS);
  const [timedOut, setTimedOut] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const scan = useCallback(async () => {
    setMessage(null);
    try {
      /*
       * ⚠️ **Die Kamera bleibt nicht länger offen als die Uhr dieser Bühne läuft** (seit
       * 2026-09-14, eigene Scanbühne — s. `platform/scanner.ts`). Vorher lief die Uhr
       * darunter weiter, während das Fenster des Fremdplugins offen stand: der Prüfer sah
       * nach dem Schliessen eine Bühne, die längst abgelaufen war.
       */
      const code = await scanCandidateCode(t.scanner.hint, TIMEOUT_SECONDS);
      // `null` heißt abgebrochen — dann bleibt die Bühne einfach stehen.
      if (!code) return;
      if (!(await selectByCode(code))) {
        // ⚠️ Ein Einstellungscode trägt Passwörter — benennen, nie im Wortlaut zeigen.
        setMessage(istEinrichtungscode(code) ? t.scanner.einrichtungscode : t.scanner.unknown(code));
      }
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    }
  }, [selectByCode, t]);

  // Auf dem Gerät geht die Kamera beim Öffnen gleich auf.
  useEffect(() => {
    if (canScan()) void scan();
  }, [scan]);

  useEffect(() => {
    if (timedOut) return;
    const id = window.setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          setTimedOut(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [timedOut]);

  const clock =
    String(Math.floor(left / 60)).padStart(2, '0') + ':' + String(left % 60).padStart(2, '0');

  return (
    <div className={styles.scanner} role="dialog" aria-modal="true" aria-label={t.scanner.ariaLabel}>
      <div className={styles.scannerHead}>
        <div style={{ flex: '1 1 auto' }}>
          <div className={styles.scannerKicker}>
            {t.header.stationPrefix} · {station.meta}
          </div>
          <div className={styles.scannerTitle}>{t.scanner.title}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className={styles.scannerKicker}>{t.scanner.window}</div>
          <div className={styles.scannerClock} data-warn={left <= 20}>
            {clock}
          </div>
        </div>
      </div>

      <div className={styles.scannerStage}>
        <div className={styles.viewfinder}>
          <div className={styles.frame}>
            <span className={styles.corner} data-at="tl" />
            <span className={styles.corner} data-at="tr" />
            <span className={styles.corner} data-at="bl" />
            <span className={styles.corner} data-at="br" />
            <span className={styles.scanLine} />
          </div>
        </div>
        <span className={styles.scannerHint}>
          {message ?? t.scanner.hint}
        </span>
      </div>

      <div className={styles.scannerButtons}>
        <button type="button" className={`tap ${styles.scannerCancel}`} onClick={closeSheet}>
          <Icon name="close" size={16} />
          <span>{t.scanner.cancel}</span>
        </button>
        {canScan() ? (
          <button
            type="button"
            className={`tap ${styles.scannerConfirm}`}
            onClick={() => void scan()}
          >
            <Icon name="qr" size={16} />
            <span>{t.scanner.scan}</span>
          </button>
        ) : (
          /*
            Ohne Kamera — also am Rechner — bleibt der Ersatzweg, damit sich der Ablauf
            entwickeln lässt. Er ist ausdrücklich als Ersatz beschriftet, nicht als Scan.
          */
          <button type="button" className={`tap ${styles.scannerConfirm}`} onClick={goToNext}>
            <Icon name="play" size={16} />
            <span>{t.scanner.noCamera}</span>
          </button>
        )}
      </div>

      {timedOut && (
        <div className={styles.timeoutOverlay}>
          <div className={styles.timeoutBox}>
            <div className={styles.timeoutTitle}>{t.scanner.timeoutTitle}</div>
            <p className={styles.timeoutText}>{t.scanner.timeoutText(TIMEOUT_SECONDS)}</p>
            <div className={styles.timeoutButtons}>
              <button type="button" className={`tap ${styles.timeoutBack}`} onClick={closeSheet}>
                <Icon name="back" size={16} />
                <span>{t.scanner.back}</span>
              </button>
              <button
                type="button"
                className={`tap ${styles.timeoutLogout}`}
                onClick={() => openSheet({ kind: 'setup' })}
              >
                <Icon name="logout" size={16} />
                <span>{t.scanner.logout}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
